const MOBILE_MAX_WIDTH = 900;
const FILTERS_SELECTOR = 'main.product-archive .wc-block-product-filters';
const OVERLAY_CONTENT_SELECTOR = '.wc-block-product-filters__overlay-content';
const CHECKBOX_SELECTOR = '.wc-block-product-filter-checkbox-list__input';
const OPEN_SELECTOR = '.wc-block-product-filters__open-overlay';
const APPLY_SELECTOR = '.wc-block-product-filters__apply';
const CLOSE_SELECTOR = '.wc-block-product-filters__close-overlay';
const INTERACTIVITY_ATTRS = ['data-wp-on--change', 'data-wp-bind--checked'];
const SCROLL_LOCK_CLASS = 'product-archive-filters-locked';

const mobileQuery = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);

let touchMoveHandler = null;
let lockedScrollY = 0;

function isMobileFilters() {
  return mobileQuery.matches;
}

function lockPageScroll() {
  if (document.documentElement.classList.contains(SCROLL_LOCK_CLASS)) {
    return;
  }

  lockedScrollY = window.scrollY;
  document.documentElement.classList.add(SCROLL_LOCK_CLASS);
  document.body.classList.add(SCROLL_LOCK_CLASS);
  document.body.style.top = `-${lockedScrollY}px`;
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';

  touchMoveHandler = (event) => {
    if (event.target.closest(OVERLAY_CONTENT_SELECTOR)) {
      return;
    }

    event.preventDefault();
  };

  document.addEventListener('touchmove', touchMoveHandler, { passive: false });
}

function unlockPageScroll() {
  if (!document.documentElement.classList.contains(SCROLL_LOCK_CLASS)) {
    return;
  }

  document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
  document.body.classList.remove(SCROLL_LOCK_CLASS);
  document.body.style.top = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.overflow = '';

  if (touchMoveHandler) {
    document.removeEventListener('touchmove', touchMoveHandler);
    touchMoveHandler = null;
  }

  window.scrollTo(0, lockedScrollY);
}

function syncOverlayScrollLock(filters) {
  if (!isMobileFilters() || !filters.classList.contains('is-overlay-opened')) {
    unlockPageScroll();
    return;
  }

  lockPageScroll();
}

function parseInputContext(input) {
  const raw = input.getAttribute('data-wp-context');

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw.replace(/&quot;/g, '"').replace(/&#039;/g, "'"));
    return parsed.item ?? null;
  } catch {
    return null;
  }
}

function isFilterParamKey(key) {
  return (
    key.startsWith('filter_')
    || key.startsWith('query_type_')
    || key === 'rating_filter'
    || key === 'filter_stock_status'
    || key === 'min_price'
    || key === 'max_price'
  );
}

function buildParamsFromOverlay(overlay) {
  const params = {};

  overlay.querySelectorAll(`${CHECKBOX_SELECTOR}:checked`).forEach((input) => {
    const item = parseInputContext(input);

    if (!item?.value || !item?.type) {
      return;
    }

    const { type, value, attributeQueryType } = item;

    if (type === 'price') {
      const [min, max] = String(value).split('|');
      if (min) {
        params.min_price = min;
      }
      if (max) {
        params.max_price = max;
      }
      return;
    }

    if (type === 'status') {
      params.filter_stock_status = params.filter_stock_status
        ? `${params.filter_stock_status},${value}`
        : value;
      return;
    }

    if (type === 'rating') {
      params.rating_filter = params.rating_filter
        ? `${params.rating_filter},${value}`
        : value;
      return;
    }

    if (type.includes('attribute/')) {
      const attributeName = type.split('/')[1];
      const paramKey = `filter_${attributeName}`;

      params[paramKey] = params[paramKey] ? `${params[paramKey]},${value}` : value;
      params[`query_type_${attributeName}`] = attributeQueryType || 'or';
      return;
    }

    if (type.includes('taxonomy/')) {
      const taxonomy = type.split('/')[1];
      const paramKey = `filter_${taxonomy}`;

      params[paramKey] = params[paramKey] ? `${params[paramKey]},${value}` : value;
    }
  });

  return params;
}

function buildFilterUrl(params) {
  const url = new URL(window.location.href);

  [...url.searchParams.keys()].forEach((key) => {
    if (isFilterParamKey(key)) {
      url.searchParams.delete(key);
    }
  });

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  return url;
}

function snapshotCheckboxes(overlay) {
  return [...overlay.querySelectorAll(CHECKBOX_SELECTOR)].map((input) => ({
    input,
    checked: input.checked,
  }));
}

function restoreCheckboxes(snapshot) {
  snapshot.forEach(({ input, checked }) => {
    input.checked = checked;
  });
}

function storeInteractivityAttrs(input) {
  INTERACTIVITY_ATTRS.forEach((attr) => {
    if (!input.hasAttribute(attr)) {
      return;
    }

    input.dataset[`stored${attr.replace(/[^a-z]/gi, '')}`] = input.getAttribute(attr);
    input.removeAttribute(attr);
  });
}

function restoreInteractivityAttrs(input) {
  INTERACTIVITY_ATTRS.forEach((attr) => {
    const key = `stored${attr.replace(/[^a-z]/gi, '')}`;
    const value = input.dataset[key];

    if (value) {
      input.setAttribute(attr, value);
      delete input.dataset[key];
    }
  });
}

function initProductArchiveFiltersMobile() {
  const filters = document.querySelector(FILTERS_SELECTOR);
  const overlay = filters?.querySelector(OVERLAY_CONTENT_SELECTOR);

  if (!filters || !overlay) {
    return;
  }

  let snapshot = snapshotCheckboxes(overlay);
  let mobileMode = false;

  const overlayObserver = new MutationObserver(() => {
    syncOverlayScrollLock(filters);
  });

  overlayObserver.observe(filters, { attributes: true, attributeFilter: ['class'] });

  const onOpenOverlay = () => {
    if (!isMobileFilters()) {
      return;
    }

    snapshot = snapshotCheckboxes(overlay);
    overlay.querySelectorAll(CHECKBOX_SELECTOR).forEach(storeInteractivityAttrs);
    window.requestAnimationFrame(() => syncOverlayScrollLock(filters));
  };

  const onCloseOverlay = () => {
    if (!isMobileFilters()) {
      return;
    }

    restoreCheckboxes(snapshot);
    overlay.querySelectorAll(CHECKBOX_SELECTOR).forEach(restoreInteractivityAttrs);
    window.requestAnimationFrame(() => syncOverlayScrollLock(filters));
  };

  const onApply = (event) => {
    if (!isMobileFilters()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const url = buildFilterUrl(buildParamsFromOverlay(overlay));

    if (url.href !== window.location.href) {
      unlockPageScroll();
      window.location.assign(url.href);
      return;
    }

    filters.classList.remove('is-overlay-opened');
    unlockPageScroll();
  };

  const onCheckboxClick = (event) => {
    if (!isMobileFilters() || !mobileMode) {
      return;
    }

    const input = event.target.matches(CHECKBOX_SELECTOR)
      ? event.target
      : event.target.closest('.wc-block-product-filter-checkbox-list__label')?.querySelector(CHECKBOX_SELECTOR);

    if (!input || !overlay.contains(input)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    input.checked = !input.checked;
  };

  const enableMobileMode = () => {
    if (mobileMode) {
      return;
    }

    mobileMode = true;
    filters.querySelector(OPEN_SELECTOR)?.addEventListener('click', onOpenOverlay, true);
    filters.querySelector(CLOSE_SELECTOR)?.addEventListener('click', onCloseOverlay, true);
    filters.querySelector(APPLY_SELECTOR)?.addEventListener('click', onApply, true);
    overlay.addEventListener('click', onCheckboxClick, true);
    syncOverlayScrollLock(filters);
  };

  const disableMobileMode = () => {
    if (!mobileMode) {
      return;
    }

    mobileMode = false;
    filters.querySelector(OPEN_SELECTOR)?.removeEventListener('click', onOpenOverlay, true);
    filters.querySelector(CLOSE_SELECTOR)?.removeEventListener('click', onCloseOverlay, true);
    filters.querySelector(APPLY_SELECTOR)?.removeEventListener('click', onApply, true);
    overlay.removeEventListener('click', onCheckboxClick, true);
    overlay.querySelectorAll(CHECKBOX_SELECTOR).forEach(restoreInteractivityAttrs);
    unlockPageScroll();
  };

  const syncMode = () => {
    if (isMobileFilters()) {
      enableMobileMode();
      return;
    }

    disableMobileMode();
  };

  syncMode();
  mobileQuery.addEventListener('change', syncMode);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductArchiveFiltersMobile);
} else {
  initProductArchiveFiltersMobile();
}
